/**
 * Service provider contracts, under the stakeholders board: every provider's
 * scope, service levels, term and notice deadline, with the signed copy
 * attached. No financial terms — see `domain/contracts.ts`.
 *
 * The record is shared the way the urgent inspection's is: each browser posts
 * its copy of the register as a note, nothing in it is ever edited (a change
 * is a new version), and the reader merges every copy by version id.
 *
 * The signed copy goes to the document library as a RESTRICTED revision of a
 * `service_contract` document — the pack declares that type restricted, so
 * distribution is the library's control, not this screen's — and a copy is
 * kept in this browser so it can be opened here regardless.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSessionIfAny } from "../../app/SessionContext";
import {
  changedTerms,
  contractProblems,
  FREQUENCY_LABEL,
  historyOf,
  KIND_LABEL,
  PROVIDER_KINDS,
  RENEWAL_LABEL,
  rowsOf,
  saveContract,
  STANDING_LABEL,
  summary,
  type Contract,
  type ContractRegister,
  type ContractRow,
  type Frequency,
  type ProviderKind,
  type Renewal,
  type ServiceLevel,
  type Standing,
} from "../../domain/contracts";
import { mockContracts } from "../../mock/contracts";
import { Button, Field, Input, Select } from "../../design/components";
import { CONTRACTS_SPEC } from "../../domain/registerSpecs";
import { RegisterNotices } from "../shared/RegisterNotices";
import { useRegisterDoc } from "../shared/useRegisterRows";
import { keepFile, readFile } from "./contractFiles";
import { t, tn } from "../../i18n";
import s from "./Contracts.module.css";

export const CONTRACT_TOPIC = "ops.contracts";

const TONE: Record<Standing, string> = {
  expired: s.bad,
  notice_due: s.warn,
  expiring: s.warn,
  no_expiry: s.warn,
  not_started: s.none,
  active: s.ok,
};

/**
 * The contracts register, kept on the platform row by row (build 5): one row
 * per version, which is only ever added, and one per contract naming its
 * latest — so two people editing the same contract are told, rather than the
 * later quietly superseding the earlier. The demo starts from the sample; a
 * screen drawn with no session shows the sample and saves nothing.
 */
export function useContracts() {
  const session = useSessionIfAny();
  const doc = useRegisterDoc(CONTRACTS_SPEC);
  const seed = useMemo(() => mockContracts(), []);
  return {
    value: session ? doc.value : seed,
    sample: !session || session.source.kind === "mock",
    doc,
    /** `base` is the register the editor opened on */
    save: (next: ContractRegister, base?: ContractRegister) => doc.commit(next, base),
  };
}

export function ContractsRegister() {
  const session = useSessionIfAny();
  const reg = useContracts();
  const rows = useMemo(() => rowsOf(reg.value, new Date()), [reg.value]);
  const sum = summary(rows);
  const [kind, setKind] = useState<"all" | ProviderKind>("all");
  const [standing, setStanding] = useState<"all" | Standing>("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditingNow] = useState<Contract | "new" | null>(null);
  /* the register as it stood when the editor opened: what the new version is made from */
  const [base, setBase] = useState<ContractRegister | null>(null);
  const setEditing = (c: Contract | "new" | null) => {
    setBase(c ? reg.value : null);
    setEditingNow(c);
  };

  const shown = rows.filter(
    (r) =>
      (kind === "all" || r.c.kind === kind) &&
      (standing === "all" || r.standing === standing) &&
      (!q.trim() ||
        `${r.c.provider} ${r.c.title} ${r.c.reference ?? ""} ${r.c.scope} ${r.c.services.join(" ")}`
          .toLowerCase()
          .includes(q.trim().toLowerCase())),
  );
  const who = () => ({
    by: session?.principal.name ?? t("this browser"),
    at: new Date().toISOString(),
    newId: () => `cv-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`,
  });

  return (
    <section className={s.wrap} data-testid="contracts">
      <div className={s.head}>
        <div>
          <h2>{t("Service provider contracts")}</h2>
          <p className={s.note}>
            {t(
              "Each provider's scope, service levels, term and notice date, with the signed copy. No prices are stored here; the signed copy is a restricted document in the library.",
            )}
          </p>
        </div>
        <Button variant="primary" onClick={() => setEditing("new")} data-testid="contract-add">
          {t("Add a contract")}
        </Button>
      </div>
      {session && <RegisterNotices doc={reg.doc} what={t("contracts register")} />}
      {reg.sample && <p className={s.sample}>{t("Sample contracts — replace them with the airport's own.")}</p>}

      <div className={s.tiles} data-testid="contract-tiles">
        <Tile n={sum.total} label={t("contracts")} sub={tn(sum.providers, "{n} provider", "{n} providers")} />
        <Tile
          n={sum.noticeDue}
          label={t("notice decisions due")}
          tone={sum.noticeDue ? "warn" : undefined}
          sub={t("within {d} days of the notice deadline", { d: 45 })}
        />
        <Tile
          n={sum.expiring}
          label={t("expiring")}
          tone={sum.expiring ? "warn" : undefined}
          sub={t("within {d} days", { d: 90 })}
        />
        <Tile
          n={sum.expired}
          label={t("expired")}
          tone={sum.expired ? "bad" : undefined}
          sub={t("a provider working without a contract in force")}
        />
        <Tile
          n={sum.noDocument}
          label={t("without a signed copy")}
          tone={sum.noDocument ? "warn" : undefined}
          sub={t("nothing to check the terms against")}
        />
        <Tile n={sum.serviceLevels} label={t("service levels recorded")} sub={t("across every contract")} />
      </div>

      <div className={s.row}>
        <Field label="Search">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("provider, service, reference")}
            aria-label={t("Search")}
          />
        </Field>
        <Field label="Type of provider">
          <Select
            value={kind}
            onChange={(e) => setKind(e.target.value as typeof kind)}
            aria-label={t("Type of provider")}
          >
            <option value="all">{t("Every type")}</option>
            {PROVIDER_KINDS.map((k) => (
              <option key={k.key} value={k.key}>
                {t(k.label)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Standing">
          <Select
            value={standing}
            onChange={(e) => setStanding(e.target.value as typeof standing)}
            aria-label={t("Standing")}
          >
            <option value="all">{t("Every contract")}</option>
            {(Object.keys(STANDING_LABEL) as Standing[]).map((k) => (
              <option key={k} value={k}>
                {t(STANDING_LABEL[k])}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <div className={s.tableWrap}>
        <table className={s.table} data-testid="contract-table">
          <thead>
            <tr>
              <th>{t("Provider")}</th>
              <th>{t("Contract")}</th>
              <th>{t("Term")}</th>
              <th>{t("Standing")}</th>
              <th>{t("Notice by")}</th>
              <th>{t("Service levels")}</th>
              <th>{t("Signed copy")}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <Fragment key={r.c.id}>
                <tr
                  className={s.clickable}
                  onClick={() => setOpen(open === r.c.id ? null : r.c.id)}
                  aria-expanded={open === r.c.id}
                  data-testid={`contract-${r.c.id}`}
                >
                  <td>
                    <b>{r.c.provider}</b>
                    <div className={s.muted}>{t(KIND_LABEL[r.c.kind])}</div>
                  </td>
                  <td>
                    {r.c.title}
                    {r.c.reference && <div className={s.muted}>{r.c.reference}</div>}
                  </td>
                  <td className={s.mono}>
                    {r.c.start ?? "—"} → {r.c.end ?? t("not recorded")}
                    {r.daysToEnd != null && (
                      <div className={s.muted}>
                        {r.daysToEnd < 0
                          ? t("ended {n} days ago", { n: -r.daysToEnd })
                          : t("{n} days left", { n: r.daysToEnd })}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={TONE[r.standing]}>{t(STANDING_LABEL[r.standing])}</span>
                    {r.flags
                      .filter((f) => f.kind === "bad")
                      .map((f) => (
                        <div key={f.what} className={s.badText}>
                          {t(f.what)}
                        </div>
                      ))}
                  </td>
                  <td className={s.mono}>
                    {r.noticeBy ?? "—"}
                    {r.daysToNotice != null && r.daysToEnd != null && r.daysToEnd >= 0 && (
                      <div className={s.muted}>
                        {r.daysToNotice < 0
                          ? t("passed {n} days ago", { n: -r.daysToNotice })
                          : t("in {n} days", { n: r.daysToNotice })}
                      </div>
                    )}
                  </td>
                  <td>{r.c.serviceLevels.length}</td>
                  <td>{r.c.document ? "✓" : <span className={s.warnText}>{t("none")}</span>}</td>
                </tr>
                {open === r.c.id && (
                  <tr>
                    <td colSpan={7}>
                      <Detail row={r} reg={reg.value} onEdit={() => setEditing(r.c)} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
        {shown.length === 0 && <p className={s.note}>{t("No contract matches.")}</p>}
      </div>

      {editing && (
        <ContractDialog
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={async (c, reason) => {
            const from = base ?? reg.value;
            const next = saveContract(from, c, who(), reason);
            await reg.save(next, from);
            setEditing(null);
            setOpen(c.id);
          }}
        />
      )}
    </section>
  );
}

function Tile({ n, label, sub, tone }: { n: number; label: string; sub: string; tone?: "warn" | "bad" }) {
  return (
    <div className={`${s.tile} ${tone === "bad" ? s.tileBad : tone === "warn" ? s.tileWarn : ""}`}>
      <b>{n}</b>
      <span>{label}</span>
      <small>{sub}</small>
    </div>
  );
}

/* ── one contract ─────────────────────────────────────────────────────── */

function Detail({ row, reg, onEdit }: { row: ContractRow; reg: ContractRegister; onEdit: () => void }) {
  const c = row.c;
  const history = historyOf(reg, c.id);
  const [said, setSaid] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => () => void (preview && URL.revokeObjectURL(preview)), [preview]);

  const openCopy = async () => {
    setSaid(null);
    const blob = c.document?.localKey ? await readFile(c.document.localKey) : null;
    if (!blob) {
      setSaid(
        c.document?.documentId
          ? t("The signed copy is in the document library, as a restricted document; open it there.")
          : t("This browser holds no copy of the signed contract."),
      );
      return;
    }
    setPreview(URL.createObjectURL(blob));
  };

  return (
    <div className={s.detail} data-testid={`contract-detail-${c.id}`}>
      <div className={s.detailHead}>
        <div>
          <h3>{c.title}</h3>
          <div className={s.muted}>
            {c.provider} · {t(KIND_LABEL[c.kind])}
            {c.reference ? ` · ${c.reference}` : ""}
          </div>
        </div>
        <div className={s.actions}>
          {c.document && (
            <Button onClick={() => void openCopy()} data-testid="contract-open-copy">
              {t("Open the signed copy")}
            </Button>
          )}
          <Button variant="primary" onClick={onEdit} data-testid="contract-edit">
            {t("Edit")}
          </Button>
        </div>
      </div>
      {said && <p className={s.note}>{said}</p>}
      {preview && (
        <div className={s.preview}>
          {c.document?.type.startsWith("image/") ? (
            <img src={preview} alt={c.document.fileName} />
          ) : (
            <iframe src={preview} title={c.document?.fileName ?? "contract"} />
          )}
          <a href={preview} download={c.document?.fileName} className={s.link}>
            {t("Save a copy")}
          </a>
        </div>
      )}
      {row.flags.length > 0 && (
        <ul className={s.flags}>
          {row.flags.map((f) => (
            <li key={f.what} className={f.kind === "bad" ? s.badText : s.warnText}>
              {t(f.what)}
            </li>
          ))}
        </ul>
      )}
      <div className={s.cols}>
        <div>
          <div className={s.cap}>{t("Scope")}</div>
          <p className={s.text}>{c.scope}</p>
          {c.services.length > 0 && (
            <>
              <div className={s.cap}>{t("Services")}</div>
              <ul className={s.list}>
                {c.services.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            </>
          )}
          {c.exclusions && (
            <>
              <div className={s.cap}>{t("Not in scope")}</div>
              <p className={s.text}>{c.exclusions}</p>
            </>
          )}
        </div>
        <div>
          <div className={s.cap}>{t("Term")}</div>
          <dl className={s.kv}>
            <dt>{t("Start")}</dt>
            <dd>{c.start ?? t("not recorded")}</dd>
            <dt>{t("Expiry")}</dt>
            <dd>{c.end ?? t("not recorded")}</dd>
            <dt>{t("Notice period")}</dt>
            <dd>{c.noticeDays != null ? tn(c.noticeDays, "{n} day", "{n} days") : t("not recorded")}</dd>
            <dt>{t("Notice by")}</dt>
            <dd>{row.noticeBy ?? "—"}</dd>
            <dt>{t("Renewal")}</dt>
            <dd>{t(RENEWAL_LABEL[c.renewal])}</dd>
            <dt>{t("Insurance expiry")}</dt>
            <dd>{c.insuranceExpiry ?? t("not recorded")}</dd>
            <dt>{t("Contract owner")}</dt>
            <dd>{c.owner ?? t("not recorded")}</dd>
            <dt>{t("Provider contact")}</dt>
            <dd>
              {[c.contact?.name, c.contact?.phone, c.contact?.email].filter(Boolean).join(" · ") || t("not recorded")}
            </dd>
          </dl>
        </div>
      </div>
      <div className={s.cap}>{t("Service levels")}</div>
      {c.serviceLevels.length === 0 ? (
        <p className={s.note}>{t("No service levels recorded.")}</p>
      ) : (
        <table className={s.table} data-testid="contract-sla">
          <thead>
            <tr>
              <th>{t("What is measured")}</th>
              <th>{t("Standard")}</th>
              <th>{t("Reported")}</th>
              <th>{t("Measured by")}</th>
              <th>{t("On a breach")}</th>
            </tr>
          </thead>
          <tbody>
            {c.serviceLevels.map((sl) => (
              <tr key={sl.id}>
                <td>{sl.measure}</td>
                <td>{sl.target}</td>
                <td>{t(FREQUENCY_LABEL[sl.frequency])}</td>
                <td>{sl.measuredBy ?? "—"}</td>
                <td>{sl.onBreach ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {c.notes && (
        <>
          <div className={s.cap}>{t("Notes")}</div>
          <p className={s.text}>{c.notes}</p>
        </>
      )}
      <div className={s.cap}>{t("History")}</div>
      <ul className={s.list} data-testid="contract-history">
        {history.map((v, i) => (
          <li key={v.id}>
            <span className={s.mono}>{new Date(v.at).toLocaleString()}</span> · {v.by} ·{" "}
            {changedTerms(history[i + 1]?.data, v.data)
              .map((x) => t(x))
              .join(", ")}
            {v.reason ? ` — ${v.reason}` : ""}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── adding and editing ───────────────────────────────────────────────── */

const blank = (): Contract => ({
  id: `ct-${Date.now().toString(36)}`,
  provider: "",
  kind: "ground_handling",
  title: "",
  scope: "",
  services: [],
  serviceLevels: [],
  renewal: "fixed",
});

function ContractDialog({
  initial,
  onClose,
  onSave,
}: {
  initial: Contract | null;
  onClose: () => void;
  onSave: (c: Contract, reason?: string) => Promise<void>;
}) {
  const session = useSessionIfAny();
  const ref = useRef<HTMLDialogElement>(null);
  const [c, setC] = useState<Contract>(() => (initial ? structuredClone(initial) : blank()));
  const [servicesText, setServicesText] = useState((initial?.services ?? []).join("\n"));
  const [file, setFile] = useState<File | null>(null);
  const [reason, setReason] = useState("");
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal?.();
  }, []);

  const draft: Contract = {
    ...c,
    services: servicesText
      .split("\n")
      .map((x) => x.trim())
      .filter(Boolean),
  };
  const problems = contractProblems(draft);
  const set = <K extends keyof Contract>(k: K, v: Contract[K]) => setC((x) => ({ ...x, [k]: v }));
  const opt = (v: string) => (v.trim() ? v : undefined);
  const setSl = (i: number, k: keyof ServiceLevel, v: string) =>
    setC((x) => ({
      ...x,
      serviceLevels: x.serviceLevels.map((sl, j) => (j === i ? { ...sl, [k]: v || undefined } : sl)),
    }));

  const save = async () => {
    setTried(true);
    if (problems.length) return;
    setBusy(true);
    setSaid(null);
    try {
      let next = draft;
      if (file) {
        const localKey = `${draft.id}/${Date.now().toString(36)}`;
        await keepFile(localKey, file);
        let documentId: string | undefined;
        let revisionId: string | undefined;
        /* the platform's copy: a restricted revision in the library. The type
           demands an expiry, so a contract without one stays in this browser
           and the screen says why. */
        if (session && draft.end) {
          try {
            const doc = await session.source.createDocument({
              type: "service_contract",
              title: `${draft.provider} — ${draft.title}`,
              siteId: session.site.id,
              reference: draft.reference,
              classification: "restricted",
              tags: ["contract", draft.kind],
            });
            const rev = await session.source.addRevision(doc.id, {
              revision: "1",
              issuedOn: draft.start,
              expiresOn: draft.end,
              changeNote: t("Signed copy, uploaded from the contracts register"),
              file,
            });
            documentId = doc.id;
            revisionId = rev.id;
          } catch (e) {
            setSaid(
              t("The document library did not take the copy ({why}); it is kept in this browser.", {
                why: e instanceof Error ? e.message : String(e),
              }),
            );
          }
        }
        next = {
          ...draft,
          document: { fileName: file.name, size: file.size, type: file.type, localKey, documentId, revisionId },
        };
      }
      await onSave(next, reason);
    } catch (e) {
      setSaid(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <dialog ref={ref} onClose={onClose} className={s.dialog} data-testid="contract-dialog">
      <h2>{initial ? t("Edit the contract") : t("Add a contract")}</h2>
      <p className={s.note}>
        {t(
          "Scope, service levels and term only. No prices; those are in the signed copy, a restricted document.",
        )}
      </p>
      <div className={s.grid}>
        <Field label="Provider">
          <Input value={c.provider} onChange={(e) => set("provider", e.target.value)} data-testid="ct-provider" />
        </Field>
        <Field label="Type of provider">
          <Select
            value={c.kind}
            onChange={(e) => set("kind", e.target.value as ProviderKind)}
            aria-label={t("Type of provider")}
          >
            {PROVIDER_KINDS.map((k) => (
              <option key={k.key} value={k.key}>
                {t(k.label)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Contract title">
          <Input value={c.title} onChange={(e) => set("title", e.target.value)} data-testid="ct-title" />
        </Field>
        <Field label="Reference">
          <Input value={c.reference ?? ""} onChange={(e) => set("reference", opt(e.target.value))} />
        </Field>
      </div>
      <Field label="Scope — what the provider does">
        <textarea
          className={s.area}
          rows={3}
          value={c.scope}
          onChange={(e) => set("scope", e.target.value)}
          data-testid="ct-scope"
        />
      </Field>
      <div className={s.grid2}>
        <Field label="Services, one per line">
          <textarea
            className={s.area}
            rows={4}
            value={servicesText}
            onChange={(e) => setServicesText(e.target.value)}
          />
        </Field>
        <Field label="Not in scope">
          <textarea
            className={s.area}
            rows={4}
            value={c.exclusions ?? ""}
            onChange={(e) => set("exclusions", opt(e.target.value))}
          />
        </Field>
      </div>

      <div className={s.cap}>{t("Service levels")}</div>
      {c.serviceLevels.map((sl, i) => (
        <div key={sl.id} className={s.slRow}>
          <Input
            placeholder={t("What is measured")}
            value={sl.measure}
            onChange={(e) => setSl(i, "measure", e.target.value)}
            aria-label={t("What is measured")}
            data-testid={`sl-measure-${i}`}
          />
          <Input
            placeholder={t("Standard, e.g. within 15 minutes for 95% of flights")}
            value={sl.target}
            onChange={(e) => setSl(i, "target", e.target.value)}
            aria-label={t("Standard")}
            data-testid={`sl-target-${i}`}
          />
          <Select
            value={sl.frequency}
            onChange={(e) => setSl(i, "frequency", e.target.value as Frequency)}
            aria-label={t("Reported")}
          >
            {(Object.keys(FREQUENCY_LABEL) as Frequency[]).map((f) => (
              <option key={f} value={f}>
                {t(FREQUENCY_LABEL[f])}
              </option>
            ))}
          </Select>
          <Input
            placeholder={t("Measured by")}
            value={sl.measuredBy ?? ""}
            onChange={(e) => setSl(i, "measuredBy", e.target.value)}
            aria-label={t("Measured by")}
          />
          <Input
            placeholder={t("On a breach, in words")}
            value={sl.onBreach ?? ""}
            onChange={(e) => setSl(i, "onBreach", e.target.value)}
            aria-label={t("On a breach")}
          />
          <button
            type="button"
            className={s.linkBtn}
            onClick={() => setC((x) => ({ ...x, serviceLevels: x.serviceLevels.filter((_, j) => j !== i) }))}
          >
            {t("Remove")}
          </button>
        </div>
      ))}
      <Button
        onClick={() =>
          setC((x) => ({
            ...x,
            serviceLevels: [
              ...x.serviceLevels,
              { id: `sl-${Date.now().toString(36)}`, measure: "", target: "", frequency: "monthly" },
            ],
          }))
        }
        data-testid="sl-add"
      >
        {t("Add a service level")}
      </Button>

      <div className={s.grid}>
        <Field label="Start">
          <Input type="date" value={c.start ?? ""} onChange={(e) => set("start", opt(e.target.value))} />
        </Field>
        <Field label="Expiry">
          <Input
            type="date"
            value={c.end ?? ""}
            onChange={(e) => set("end", opt(e.target.value))}
            data-testid="ct-end"
          />
        </Field>
        <Field label="Notice period, days">
          <Input
            type="number"
            min={0}
            value={c.noticeDays ?? ""}
            onChange={(e) => set("noticeDays", e.target.value === "" ? undefined : Number(e.target.value))}
            data-testid="ct-notice"
          />
        </Field>
        <Field label="Renewal">
          <Select
            value={c.renewal}
            onChange={(e) => set("renewal", e.target.value as Renewal)}
            aria-label={t("Renewal")}
          >
            {(Object.keys(RENEWAL_LABEL) as Renewal[]).map((k) => (
              <option key={k} value={k}>
                {t(RENEWAL_LABEL[k])}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Insurance expiry">
          <Input
            type="date"
            value={c.insuranceExpiry ?? ""}
            onChange={(e) => set("insuranceExpiry", opt(e.target.value))}
          />
        </Field>
        <Field label="Contract owner">
          <Input value={c.owner ?? ""} onChange={(e) => set("owner", opt(e.target.value))} />
        </Field>
        <Field label="Provider contact">
          <Input
            value={c.contact?.name ?? ""}
            onChange={(e) => set("contact", { ...c.contact, name: opt(e.target.value) })}
          />
        </Field>
        <Field label="Phone">
          <Input
            value={c.contact?.phone ?? ""}
            onChange={(e) => set("contact", { ...c.contact, phone: opt(e.target.value) })}
          />
        </Field>
        <Field label="E-mail">
          <Input
            value={c.contact?.email ?? ""}
            onChange={(e) => set("contact", { ...c.contact, email: opt(e.target.value) })}
          />
        </Field>
      </div>
      <Field label="Notes">
        <textarea
          className={s.area}
          rows={2}
          value={c.notes ?? ""}
          onChange={(e) => set("notes", opt(e.target.value))}
        />
      </Field>
      <div className={s.grid2}>
        <Field label="Signed copy — PDF, Word or an image">
          <input
            type="file"
            accept=".pdf,.doc,.docx,image/*"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            data-testid="ct-file"
          />
        </Field>
        {initial && (
          <Field label="Why it changed (optional)">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        )}
      </div>
      {c.document && !file && (
        <p className={s.note}>
          {t("Attached: {name}. Choose a file to replace it; the earlier copy stays in the history.", {
            name: c.document.fileName,
          })}
        </p>
      )}
      {tried && problems.length > 0 && (
        <ul className={s.problems} role="alert">
          {problems.map((p) => (
            <li key={p.message + (p.vars?.text ?? "")}>{t(p.message, p.vars)}</li>
          ))}
        </ul>
      )}
      {said && <p className={s.warnText}>{said}</p>}
      <div className={s.foot}>
        <Button onClick={onClose} disabled={busy}>
          {t("Cancel")}
        </Button>
        <Button variant="primary" onClick={() => void save()} disabled={busy} data-testid="ct-save">
          {busy ? t("Saving…") : t("Save the contract")}
        </Button>
      </div>
    </dialog>
  );
}
